import splashConfig from "../config/splash";

const DEFAULT_STYLE = {
  position:         'fixed',
  inset:            '0',
  zIndex:           '9999',
  display:          'flex',
  justifyContent:   'center',
  alignItems:       'center',
  backgroundColor:  'transparent',
  pointerEvents:    'all',
};

/**
 * Full‐screen splash page custom element.
 * @extends HTMLElement
 * @property {Object} cfg - Animation config for the selected provider.
 * @property {number} _timer - ID of the transition timeout.
 */
class SplashPage extends HTMLElement {
  constructor() {
    super();
    const tpl = document.createElement('template');
    tpl.innerHTML = this.innerHTML.trim();
    this.shadow = this.attachShadow({ mode: 'open' });
    this.shadow.appendChild(tpl.content.cloneNode(true));
    Object.assign(this.style, DEFAULT_STYLE);
  }

  /**
   * Invoked when element is added to the document.
   */
  connectedCallback() {
    const name = this.getAttribute('name') || 'default';
    this.cfg = splashConfig[name];

    if (!this.cfg?.animations) {
      return this.remove();
    }

    const { reveal = [], timeout } = this.cfg.animations;

    reveal.forEach(({ selector }) => {
      const els = document.querySelectorAll(selector);
      this._toggleHidden(els, true);
    });

    const trigger = this.shadow.querySelector('.js-splash-trigger');
    trigger?.addEventListener('click', e => {
      e.preventDefault();
      this._transition();
    });

    this._timer = setTimeout(() => this._transition(), timeout);
  }

  /**
   * Invoked when element is removed from the document.
   */
  disconnectedCallback() {
    clearTimeout(this._timer);
  }

  /**
   * Perform hide animations, then reveal real content and remove splash.
   * @private
   */
  _transition() {
    clearTimeout(this._timer);
    const { hide = [], reveal = [] } = (this.cfg?.animations ?? {});

    const hidePromises = hide.flatMap(({ selector, classes = [], delay = 0 }) => {
      const els = this.shadow.querySelectorAll(selector);
      this._applyClasses(els, classes, delay);
      return this._waitForAnimations(els);
    });

    Promise.all(hidePromises).then(() => {
      reveal.forEach(({ selector, classes = [], delay = 0 }) => {
        const els = document.querySelectorAll(selector);
        this._toggleHidden(els, false);
        this._applyClasses(els, classes, delay);
        this._waitForAnimations(els, classes);
      });
      
      setTimeout(() => this.remove(), 100);
    });
  }

  /**
   * Add CSS classes and optional delay to elements.
   * @param {NodeList|HTMLElement[]} elements - Elements to animate.
   * @param {string[]} classes - Animation CSS classes.
   * @param {number} [delay=0] - Delay before starting (ms).
   * @private
   */
  _applyClasses(elements, classes, delay = 0) {
    const els = /** @type {HTMLElement[]} */ (elements);
    els.forEach(el => {
      if (delay) el.style.setProperty('--delay', `${delay}ms`);
      el.classList.add('animated', ...classes);
    });
  }

  /**
   * Toggle the 'js-hidden' helper class on elements.
   * @param {NodeList|HTMLElement[]} elements - Elements to animate.
   * @param {boolean} [hide=true] - add if true, remove if false.
   * @private
   */
  _toggleHidden(elements, hide = true) {
    const els = /** @type {HTMLElement[]} */ (elements);
    els.forEach(el => {
      el.classList[hide ? 'add' : 'remove']('js-hidden');
    });
  }

  /**
   * Return an array of promises resolving when each element's animation ends.
   * @param {NodeList|HTMLElement[]} elements - Elements to animate.
   * @param {string[]} classes - Animation CSS classes.
   * @returns {Promise<void>[]}
   * @private
   */
  _waitForAnimations(elements, classes = []) {
    const els = /** @type {HTMLElement[]} */ (Array.from(elements));
    return els.map(el =>
      new Promise(resolve => {
        el.addEventListener('animationend', () => {
          el.classList.remove('animated', ...classes);
          resolve();
        }, { once: true });
      })
    );
  }
}

customElements.define('splash-page', SplashPage);
